import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../SEO/SEOHead';

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const expertiseAreas = [
    {
      title: 'Fractional CTO & Technical Leadership',
      description:
        'Executive-level technology strategy without the full-time overhead. Board-ready roadmaps, vendor evaluations, and hands-on architecture reviews.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
    {
      title: 'AI Strategy & LLM Integration',
      description:
        'From proof-of-concept to production. RAG pipelines, agentic workflows, fine-tuning strategy, and responsible AI governance frameworks.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      title: 'Cloud Architecture & Multi-Cloud Strategy',
      description:
        'Deep expertise across AWS, GCP, and Azure. Infrastructure as code, cost optimization, and migration strategies that minimize risk.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
        </svg>
      ),
    },
    {
      title: 'Data Analytics & Business Intelligence',
      description:
        'Data pipelines, warehousing, and visualization that turn raw information into strategic advantage. BigQuery, Snowflake, dbt, and Looker.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      title: 'DevOps & Security (SOC 2, CI/CD)',
      description:
        'Automated pipelines, infrastructure hardening, SOC 2 compliance, and operational excellence. Security as a first-class engineering concern.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
    },
    {
      title: 'Team Building & Engineering Culture',
      description:
        'Hiring frameworks, performance systems, and engineering culture that retains top talent. Build teams that ship with confidence.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    },
  ];

  const credentials = [
    { value: '15+', label: 'Years in Technology Leadership' },
    { value: '100+', label: 'Projects Delivered' },
    { value: '3', label: 'Major Cloud Platforms (AWS, GCP, Azure)' },
    { value: 'Startup to Fortune 500', label: 'Enterprise Client Range' },
  ];

  const philosophyItems = [
    {
      title: 'The Fractional Advantage',
      description:
        'Most companies between $5M and $50M in revenue need executive-level technology leadership, but not a full-time CTO on the payroll. The fractional model gives you seasoned strategic guidance at a fraction of the cost, without sacrificing depth or accountability.',
    },
    {
      title: 'Outcomes Over Headcount',
      description:
        'The goal is never to build the biggest team. It is to build the right team, ship the right product, and hit the business milestones that actually matter. Every recommendation ties back to measurable outcomes.',
    },
    {
      title: 'Knowledge Transfer, Not Dependency',
      description:
        'A great fractional CTO builds capability within your organization, not a dependency on external consultants. Processes, documentation, and mentorship ensure your team grows stronger with every engagement.',
    },
    {
      title: 'Technology as a Business Enabler',
      description:
        'Technology is not a cost center. When aligned with business strategy, it becomes the single greatest lever for growth, efficiency, and competitive advantage. That alignment is the core of every engagement.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="About North - Fractional CTO & Engineering Leader"
        description="Meet the founder of North, with 15+ years of experience in cloud architecture, AI strategy, and engineering leadership. Fractional CTO serving startups and mid-market companies."
        path="/about"
      />

      {/* Structured Data: BreadcrumbList + Person Schema */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: 'https://www.northinteractive.co',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'About',
                item: 'https://www.northinteractive.co/about',
              },
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Person',
            name: 'North',
            jobTitle: 'Fractional CTO & Technology Consulting',
            description:
              'Fractional CTO firm with 15+ years of experience in cloud architecture, AI strategy, data analytics, and engineering leadership.',
            url: 'https://www.northinteractive.co/about',
            sameAs: [],
            worksFor: {
              '@type': 'Organization',
              name: 'North',
              url: 'https://www.northinteractive.co',
            },
            knowsAbout: [
              'Fractional CTO Services',
              'AI Strategy',
              'Large Language Models',
              'Cloud Architecture',
              'Amazon Web Services (AWS)',
              'Google Cloud Platform (GCP)',
              'Microsoft Azure',
              'Data Analytics',
              'Business Intelligence',
              'DevOps',
              'SOC 2 Compliance',
              'Engineering Leadership',
              'Team Building',
              'Multi-Cloud Strategy',
              'Kubernetes',
              'Infrastructure as Code',
              'Terraform',
              'CI/CD Pipelines',
              'Agile Methodologies',
              'Product Development',
            ],
          })}
        </script>
      </Helmet>

      {/* ===== HERO SECTION ===== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-pink-50 via-white to-cream pt-32 pb-20 px-6">
        {/* Background Blobs */}
        <div className="absolute inset-0 opacity-40">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full mb-8 border border-gray-100"
            >
              <span className="text-sm font-medium text-muted-grey">
                Founder & Fractional CTO
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-dark-slate mb-6 tracking-tight leading-none"
            >
              About North
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-xl md:text-2xl text-muted-grey max-w-3xl mx-auto leading-relaxed"
            >
              Engineering leadership built on 15+ years of shipping software,
              scaling teams, and turning complex technology into measurable business results.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ===== ABOUT THE FOUNDER SECTION ===== */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            {/* Left Column - Bio */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center px-4 py-2 bg-cream rounded-full mb-6 border border-gray-100"
              >
                <span className="text-sm font-medium text-muted-grey">
                  Meet the Founder
                </span>
              </motion.div>

              <h2 className="text-4xl md:text-5xl font-bold text-dark-slate mb-6 tracking-tight leading-tight">
                The Founder
                <br />
                <span className="text-muted-blue-grey">VP of Engineering</span>
              </h2>

              <div className="space-y-5 text-lg text-muted-grey leading-relaxed">
                <p>
                  The founder of North is a{' '}
                  <span className="font-semibold text-dark-slate">
                    VP of Engineering
                  </span>{' '}
                  who leads teams building cloud-native solutions for enterprises
                  navigating digital transformation. With over 15 years in engineering
                  leadership and technology consulting, he has developed a rare combination
                  of executive-level strategic thinking and deep hands-on technical expertise.
                </p>
                <p>
                  His experience spans the full modern technology stack:{' '}
                  <span className="font-semibold text-dark-slate">
                    cloud architecture across AWS, GCP, and Azure
                  </span>
                  , AI/ML strategy and LLM integration, data analytics and business
                  intelligence, DevOps automation, and security compliance. He does not
                  just advise from a whiteboard. He architects solutions, writes code,
                  configures infrastructure, and deploys to production.
                </p>
                <p>
                  Over the course of{' '}
                  <span className="font-semibold text-dark-slate">
                    100+ projects delivered
                  </span>
                  , he has worked with organizations ranging from early-stage startups
                  to Fortune 500 enterprises. He founded North to bring that same caliber
                  of leadership to companies that need it most: startups and mid-market
                  businesses in the{' '}
                  <span className="font-semibold text-dark-slate">
                    $5M to $50M revenue range
                  </span>{' '}
                  that are ready to scale but lack the in-house technical leadership to
                  do it confidently.
                </p>
                <p>
                  Through fractional CTO engagements, North embeds directly with
                  leadership teams to set technical direction, evaluate architecture
                  decisions, hire and mentor engineers, and ensure that every technology
                  investment maps to a tangible business outcome.
                </p>
              </div>
            </motion.div>

            {/* Right Column - Credentials Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-dark-navy to-deep-charcoal rounded-3xl p-12 text-white shadow-2xl">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-warm-tan opacity-20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-muted-blue-grey opacity-20 rounded-full blur-3xl"></div>

                <div className="relative z-10">
                  <h3 className="text-3xl font-bold mb-8">
                    Credentials & Experience
                  </h3>

                  <div className="space-y-8">
                    {credentials.map((stat, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        className="border-b border-white/20 pb-6 last:border-0"
                      >
                        <div className="text-4xl font-bold mb-2">
                          {stat.value}
                        </div>
                        <div className="text-gray-300 text-lg">
                          {stat.label}
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="mt-10 pt-8 border-t border-white/20">
                    <motion.a
                      href="https://calendar.app.google/ube4SfQSbn6SqRCCA"
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-6 py-3 bg-white text-dark-navy rounded-full font-semibold hover:bg-gray-100 transition-all inline-flex items-center"
                    >
                      Book a Strategy Call
                    </motion.a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== PHILOSOPHY SECTION ===== */}
      <section className="py-24 px-6 bg-cream">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 bg-white rounded-full mb-6 border border-gray-100"
            >
              <span className="text-sm font-medium text-muted-grey">
                How We Work
              </span>
            </motion.div>

            <h2 className="text-4xl md:text-5xl font-bold text-dark-slate mb-6 tracking-tight">
              Philosophy &
              <br />
              <span className="text-muted-blue-grey">Approach</span>
            </h2>

            <p className="text-xl text-muted-grey max-w-3xl mx-auto leading-relaxed">
              The principles that guide every engagement and every technical decision at North.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {philosophyItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm"
              >
                <h3 className="text-2xl font-bold text-dark-slate mb-4 tracking-tight">
                  {item.title}
                </h3>
                <p className="text-muted-grey leading-relaxed text-lg">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== AREAS OF EXPERTISE SECTION ===== */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 bg-cream rounded-full mb-6 border border-gray-100"
            >
              <span className="text-sm font-medium text-muted-grey">
                Core Expertise
              </span>
            </motion.div>

            <h2 className="text-4xl md:text-5xl font-bold text-dark-slate mb-6 tracking-tight">
              Areas of
              <br />
              <span className="text-muted-blue-grey">Expertise</span>
            </h2>

            <p className="text-xl text-muted-grey max-w-3xl mx-auto leading-relaxed">
              Deep, hands-on experience across the disciplines that matter most
              for modern technology organizations.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {expertiseAreas.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="group bg-white rounded-3xl p-8 border border-gray-100 hover:border-gray-200 shadow-sm hover:shadow-lg transition-all"
              >
                <div className="mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-dark-navy to-muted-blue-grey rounded-2xl flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                    {area.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-dark-slate mb-3 tracking-tight group-hover:text-dark-navy transition-colors">
                  {area.title}
                </h3>
                <p className="text-muted-grey leading-relaxed">
                  {area.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== INTERNAL LINKS SECTION ===== */}
      <section className="py-16 px-6 bg-cream">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Link
                to="/blog"
                className="group block bg-white rounded-3xl p-8 border border-gray-100 hover:border-gray-200 shadow-sm hover:shadow-lg transition-all"
              >
                <h3 className="text-xl font-bold text-dark-slate mb-3 tracking-tight group-hover:text-dark-navy transition-colors">
                  Read the North Blog
                </h3>
                <p className="text-muted-grey leading-relaxed mb-4">
                  Read my latest thinking on the North Blog. Technical perspectives
                  on cloud strategy, AI adoption, and engineering leadership.
                </p>
                <span className="text-dark-navy font-semibold inline-flex items-center">
                  Explore Articles
                  <svg
                    className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </span>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Link
                to="/services"
                className="group block bg-white rounded-3xl p-8 border border-gray-100 hover:border-gray-200 shadow-sm hover:shadow-lg transition-all"
              >
                <h3 className="text-xl font-bold text-dark-slate mb-3 tracking-tight group-hover:text-dark-navy transition-colors">
                  Our Service Offerings
                </h3>
                <p className="text-muted-grey leading-relaxed mb-4">
                  View our full service offerings. From fractional CTO engagements
                  to AI implementation and cloud architecture.
                </p>
                <span className="text-dark-navy font-semibold inline-flex items-center">
                  View Services
                  <svg
                    className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </span>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="py-24 px-6 bg-gradient-to-br from-dark-navy to-deep-charcoal">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Ready to Talk Strategy?
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed mb-10">
              Whether you need a fractional CTO, an AI roadmap, or a cloud
              migration strategy, it starts with a conversation. No pitch decks,
              no pressure. Just an honest assessment of where technology can
              move your business forward.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.a
                href="https://calendar.app.google/ube4SfQSbn6SqRCCA"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-white text-dark-navy rounded-full font-semibold text-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl inline-block"
              >
                Book a Strategy Call
              </motion.a>
              <motion.a
                href="mailto:info@northinteractive.co"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-transparent text-white rounded-full font-semibold text-lg border-2 border-white/30 hover:border-white/60 transition-all inline-block"
              >
                Send an Email
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
